import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Spin } from 'antd';
import { Provider, useSelector, useDispatch } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from './store';
import { checkAuth, logout } from './store/slices/authSlice';
import ErrorBoundary from './components/ErrorBoundary';
import MobileNotSupported from './components/MobileNotSupported';
import Footer from './components/Footer';
import useMobileDetection from './hooks/useMobileDetection';
import {
  UserOutlined, 
  LogoutOutlined, 
  DashboardOutlined,
  TeamOutlined,
  DatabaseOutlined,
  SoundOutlined,
  GlobalOutlined,
  DollarOutlined,
  MenuOutlined,
  VideoCameraOutlined,
  PlayCircleOutlined,
  BarChartOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { Button, Dropdown } from 'antd';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import UserManagement from './components/UserManagement';
import TikTokManagement from './components/TikTokManagement';
import VideoTracking from './components/VideoTracking';
import Profile from './components/Profile';
import AdminOverview from './components/AdminOverview';
import './App.css';

const { Header, Content, Sider } = Layout;

function AppContent() {
  const dispatch = useDispatch();
  const { user, isAuthenticated, isLoading } = useSelector(state => state.auth);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useMobileDetection();

  // Check authentication on mount
  useEffect(() => {
    dispatch(checkAuth());
  }, [dispatch]);

  // Show mobile not supported page for mobile devices
  if (isMobile) {
    return <MobileNotSupported />;
  }

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated || !user) {
    return <Login />;
  }

  const handleLogout = () => {
    dispatch(logout());
  };

  const userMenu = {
    items: [
      {
        key: 'profile',
        icon: <UserOutlined />,
        label: 'Profile',
        onClick: () => navigate('/profile')
      },
      {
        type: 'divider'
      },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: 'Logout',
        onClick: handleLogout
      }
    ]
  };

  // Role-based menu items
  const getMenuItems = () => {
    // Ensure user object exists
    if (!user) {
      return [];
    }

    const baseItems = [
      {
        key: '/dashboard',
        icon: <DashboardOutlined />,
        label: 'Dashboard',
      },
    ];

    // User-only items (not for admin)
    const userItems = [
      {
        key: '/tiktok-management',
        icon: <VideoCameraOutlined />,
        label: 'TikTok Management',
      },
      {
        key: '/video-tracking',
        icon: <BarChartOutlined />,
        label: 'Video Tracking',
      },
      {
        key: '/profile',
        icon: <UserOutlined />,
        label: 'Profile',
      },
    ];

    // Admin-only items
    const adminItems = [
      {
        key: '/users',
        icon: <TeamOutlined />,
        label: 'Users',
      },
      {
        key: '/admin-overview',
        icon: <SettingOutlined />,
        label: 'Admin Overview',
      },
      {
        key: '/tiktok-management',
        icon: <VideoCameraOutlined />,
        label: 'TikTok Management',
      },
      {
        key: '/video-tracking',
        icon: <BarChartOutlined />,
        label: 'Video Tracking',
      },
    ];

    // Admin gets baseItems + adminItems
    // Regular users get baseItems + userItems (Profile)
    return user?.is_admin ? [...baseItems, ...adminItems] : [...baseItems, ...userItems];
  };

  const menuItems = getMenuItems() || [];
  const handleMenuClick = ({ key }) => {
    navigate(key);
    setMobileMenuVisible(false);
  };

  return (
    <Layout style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header className="app-header">
        <div className="header-left">
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setMobileMenuVisible(!mobileMenuVisible)}
            className="mobile-menu-button"
            style={{ color: 'white', fontSize: '18px' }}
          />
          <div className="logo">
            Tiktok Checker
          </div>
        </div>
        <div className="user-info">
          <span className="welcome-text">Welcome, {user?.username || 'User'}</span>
          {user?.is_admin && <span className="admin-badge">ADMIN</span>}
            <Dropdown menu={userMenu} placement="bottomRight">
            <Button type="text" style={{ color: 'white' }}>
              <UserOutlined />
            </Button>
          </Dropdown>
        </div>
      </Header>
      
      <Layout style={{ flex: 1 }}>
        <Sider
          width={200}
          collapsed={collapsed}
          onCollapse={setCollapsed}
          className="desktop-sider"
          style={{
            background: '#fff',
          }}
        >
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems || []}
            onClick={handleMenuClick}
            style={{ height: '100%', borderRight: 0 }}
          />
        </Sider>

        {/* Mobile Menu */}
        {mobileMenuVisible && (
          <div className="mobile-menu-overlay" onClick={() => setMobileMenuVisible(false)}>
            <div className="mobile-menu" onClick={(e) => e.stopPropagation()}>
              <Menu
                mode="inline"
                selectedKeys={[location.pathname]}
                items={menuItems || []}
                onClick={handleMenuClick}
                style={{ border: 'none' }}
              />
            </div>
          </div>
        )}

        <Layout style={{ padding: '0 24px 24px', flex: 1 }}>
          <Content className="app-content">
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              {!user?.is_admin && (
                <>
                  <Route path="/tiktok-management" element={<TikTokManagement />} />
                  <Route path="/video-tracking" element={<VideoTracking />} />
                  <Route path="/profile" element={<Profile />} />
                </>
              )}
      {user?.is_admin && (
        <>
          <Route path="/users" element={<UserManagement />} />
          <Route path="/admin-overview" element={<AdminOverview />} />
          <Route path="/tiktok-management" element={<TikTokManagement />} />
          <Route path="/video-tracking" element={<VideoTracking />} />
        </>
      )}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Content>
        </Layout>
      </Layout>
      
      <Footer />
    </Layout>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <PersistGate loading={<Spin size="large" />} persistor={persistor}>
          <Router>
            <AppContent />
          </Router>
        </PersistGate>
      </Provider>
    </ErrorBoundary>
  );
}

export default App;